import { createFileRoute, Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { ArrowLeft, ArrowDown, ArrowUp, Eye, EyeOff, FolderTree, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react'
import {
  useProductCategories,
  useCreateProductCategory,
  useUpdateProductCategory,
  useDeleteProductCategory,
  useToggleProductCategory,
  useReorderProductCategories,
} from '@/lib/productCatalog'
import { productMediaApi } from '@/api/productMedia'
import { userMessage } from '@/api/errors'
import { toast } from '@/ui/Toast'
import type { Category, CategoryInput } from '@/types/ecommerce'
import { InlineToggle } from '@/components/crud/InlineToggle'
import EditModal, { type EditField } from '@/components/crud/EditModal'
import { useDeleteConfirm } from '@/components/crud/DeleteConfirm'
import { resolveMediaUrl } from '@/utils/mediaUrl'

/** Depth-first walk so children render directly under their parent, indented. */
function flattenTree(categories: Category[]): { category: Category; depth: number }[] {
  const byParent = new Map<string, Category[]>()
  for (const c of categories) {
    const key = c.parentId ?? '__root__'
    if (!byParent.has(key)) byParent.set(key, [])
    byParent.get(key)!.push(c)
  }
  const out: { category: Category; depth: number }[] = []
  const walk = (parentKey: string, depth: number) => {
    for (const c of byParent.get(parentKey) ?? []) {
      out.push({ category: c, depth })
      walk(c.id, depth + 1)
    }
  }
  walk('__root__', 0)
  return out
}

/** Every descendant of `excludeId` (inclusive) — a category can't be parented under its own subtree. */
function descendantIds(categories: Category[], excludeId: string): Set<string> {
  const out = new Set<string>([excludeId])
  let changed = true
  while (changed) {
    changed = false
    for (const c of categories) {
      if (c.parentId && out.has(c.parentId) && !out.has(c.id)) {
        out.add(c.id)
        changed = true
      }
    }
  }
  return out
}

/** Move `id` one slot up/down against its immediate neighbor in `rows` — only when that
 * neighbor is a sibling (same parentId); a category can't swap places with a different
 * tree level. Returns the full category id list in its new order (SortOrder becomes each
 * id's index), or null if the move isn't possible (top/bottom of its sibling group). */
function moveCategory(rows: { category: Category; depth: number }[], id: string, direction: 'up' | 'down'): string[] | null {
  const index = rows.findIndex((r) => r.category.id === id)
  if (index === -1) return null
  const swapWith = direction === 'up' ? index - 1 : index + 1
  if (swapWith < 0 || swapWith >= rows.length) return null
  if (rows[swapWith].category.parentId !== rows[index].category.parentId) return null

  const reordered = [...rows]
  ;[reordered[index], reordered[swapWith]] = [reordered[swapWith], reordered[index]]
  return reordered.map((r) => r.category.id)
}

function CategoriesPage() {
  const { data: categories = [], isLoading, isError, error, isFetching, refetch } = useProductCategories()
  const create = useCreateProductCategory()
  const update = useUpdateProductCategory()
  const del = useDeleteProductCategory()
  const toggle = useToggleProductCategory()
  const reorder = useReorderProductCategories()
  const confirmDelete = useDeleteConfirm()

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Category | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkBusy, setBulkBusy] = useState(false)

  const rows = useMemo(() => flattenTree(categories), [categories])
  const nameById = useMemo(() => new Map(categories.map((c) => [c.id, c.name])), [categories])

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.category.id))

  function toggleSelected(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  /** The backend only exposes a toggle (PUT /categories/{id}/toggle), not "set to X" — so a bulk
   * action flips exactly the selected categories that aren't already in the target state. Run
   * sequentially rather than in parallel: each call is a tiny write, and a burst of parallel
   * writes is what trips the gateway's rate limiter. */
  async function bulkSetActive(target: boolean) {
    const ids = categories.filter((c) => selected.has(c.id) && c.isActive !== target).map((c) => c.id)
    if (ids.length === 0) {
      toast.info(`Nothing to change`, `Every selected category is already ${target ? 'active' : 'disabled'}.`)
      return
    }
    setBulkBusy(true)
    let done = 0
    try {
      for (const id of ids) {
        await toggle.mutateAsync(id)
        done++
      }
      toast.success(`${done} categor${done === 1 ? 'y' : 'ies'} ${target ? 'activated' : 'disabled'}`)
      setSelected(new Set())
    } catch (err) {
      toast.error(`Stopped after ${done} of ${ids.length}`, userMessage(err))
    } finally {
      setBulkBusy(false)
    }
  }

  function handleMove(id: string, direction: 'up' | 'down') {
    const orderedIds = moveCategory(rows, id, direction)
    if (orderedIds) reorder.mutate(orderedIds)
  }

  const parentOptions = (excludeId?: string) => {
    const blocked = excludeId ? descendantIds(categories, excludeId) : new Set<string>()
    return [
      { value: '', label: '— None (top level) —' },
      ...categories.filter((c) => !blocked.has(c.id)).map((c) => ({ value: c.id, label: c.name })),
    ]
  }

  const fields = (excludeId?: string): EditField[] => [
    {
      key: 'imageUrl',
      label: 'Image',
      type: 'file',
      accept: 'image/jpeg,image/png,image/gif,image/webp',
      previewKind: 'image',
      uploadFn: (file) => productMediaApi.uploadCategoryImage(file),
      hint: 'JPEG, PNG, GIF or WEBP, up to 8MB.',
    },
    { key: 'name', label: 'Name', required: true },
    { key: 'slug', label: 'Slug', placeholder: 'Auto-generated from name if left blank' },
    { key: 'parentId', label: 'Parent category', type: 'select', options: parentOptions(excludeId) },
    { key: 'description', label: 'Description', type: 'textarea' },
    { key: 'iconUrl', label: 'Icon URL', placeholder: 'https://… (small nav/sidebar icon, distinct from the image above)' },
    { key: 'metaTitle', label: 'SEO: Meta title', placeholder: 'Defaults to the category name if left blank' },
    { key: 'metaDescription', label: 'SEO: Meta description', type: 'textarea' },
  ]

  const toInput = (values: Record<string, any>): CategoryInput => ({
    name: values.name,
    slug: values.slug || undefined,
    parentId: values.parentId || null,
    description: values.description || undefined,
    imageUrl: values.imageUrl || null,
    iconUrl: values.iconUrl || null,
    metaTitle: values.metaTitle || null,
    metaDescription: values.metaDescription || null,
  })

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <FolderTree className="w-6 h-6" /> Product Categories
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Category tree used to organize the product catalog.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/products"
            className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="w-4 h-4" /> Products
          </Link>
          <button onClick={() => refetch()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setCreating(true)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm">
            <Plus className="w-4 h-4" /> New Category
          </button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm dark:border-blue-900 dark:bg-blue-900/20">
          <span className="font-medium text-blue-800 dark:text-blue-200">{selected.size} selected</span>
          <button
            onClick={() => bulkSetActive(true)}
            disabled={bulkBusy}
            className="flex items-center gap-1 rounded-lg bg-white px-3 py-1.5 text-green-700 shadow-sm hover:bg-green-50 disabled:opacity-50 dark:bg-gray-800 dark:text-green-400"
          >
            <Eye className="h-4 w-4" /> Activate
          </button>
          <button
            onClick={() => bulkSetActive(false)}
            disabled={bulkBusy}
            className="flex items-center gap-1 rounded-lg bg-white px-3 py-1.5 text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 dark:bg-gray-800 dark:text-gray-300"
          >
            <EyeOff className="h-4 w-4" /> Disable
          </button>
          <button onClick={() => setSelected(new Set())} disabled={bulkBusy} className="ml-auto text-blue-700 hover:underline dark:text-blue-300">
            Clear selection
          </button>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading categories…</p>
        ) : isError ? (
          <p className="p-6 text-red-600">Failed to load categories: {(error as Error)?.message}.</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-gray-500">No categories found.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-4 py-2 w-8">
                  <input
                    type="checkbox"
                    aria-label="Select all categories"
                    checked={allSelected}
                    onChange={() => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.category.id)))}
                  />
                </th>
                <th className="px-4 py-2">Preview</th>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Slug</th>
                <th className="px-4 py-2">Parent</th>
                <th className="px-4 py-2">Products</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Order</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ category: c, depth }) => {
                const siblings = rows.filter((r) => r.category.parentId === c.parentId)
                const siblingIndex = siblings.findIndex((r) => r.category.id === c.id)
                return (
                <tr key={c.id} className={`border-t border-gray-100 dark:border-gray-700 ${!c.isActive ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      aria-label={`Select ${c.name}`}
                      checked={selected.has(c.id)}
                      onChange={() => toggleSelected(c.id)}
                    />
                  </td>
                  <td className="px-4 py-3">
                    {c.imageUrl ? (
                      <img src={resolveMediaUrl(c.imageUrl)} alt="" className="h-10 w-10 rounded-md object-cover bg-gray-100 dark:bg-gray-700" />
                    ) : (
                      <div className="h-10 w-10 rounded-md bg-gray-100 dark:bg-gray-700" />
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">
                    <span style={{ paddingLeft: `${depth * 1.25}rem` }}>{depth > 0 ? '— ' : ''}{c.name}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 font-mono">{c.slug}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{c.parentId ? (nameById.get(c.parentId) ?? '—') : '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{c.productCount}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <InlineToggle
                        checked={c.isActive}
                        onToggle={async () => {
                          await toggle.mutateAsync(c.id)
                        }}
                      />
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          c.isActive
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                        }`}
                      >
                        {c.isActive ? 'Active' : 'Disabled'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={() => handleMove(c.id, 'up')}
                        disabled={siblingIndex === 0}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 disabled:opacity-30"
                        title="Move up"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleMove(c.id, 'down')}
                        disabled={siblingIndex === siblings.length - 1}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 disabled:opacity-30"
                        title="Move down"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setEditing(c)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Edit category">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          confirmDelete(c.name, async () => {
                            await del.mutateAsync(c.id)
                          })
                        }
                        className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                        title="Delete category"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        )}
      </div>

      <EditModal
        isOpen={creating}
        onClose={() => setCreating(false)}
        title="New Category"
        fields={fields()}
        initialValues={{ parentId: '' }}
        submitLabel="Create"
        onSubmit={async (values) => {
          try {
            await create.mutateAsync(toInput(values))
          } catch (err) {
            throw new Error(userMessage(err))
          }
        }}
      />

      <EditModal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title="Edit Category"
        description={editing?.name}
        fields={fields(editing?.id)}
        initialValues={
          editing
            ? {
                name: editing.name,
                slug: editing.slug,
                parentId: editing.parentId ?? '',
                description: editing.description ?? '',
                imageUrl: editing.imageUrl ?? '',
                iconUrl: editing.iconUrl ?? '',
                metaTitle: editing.metaTitle ?? '',
                metaDescription: editing.metaDescription ?? '',
              }
            : {}
        }
        onSubmit={async (values) => {
          if (!editing) return
          try {
            await update.mutateAsync({ id: editing.id, body: toInput(values) })
          } catch (err) {
            throw new Error(userMessage(err))
          }
        }}
      />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/products/categories/')({
  component: CategoriesPage,
})
